import React, { useState } from 'react';
import { useSEO, getBaseUrl } from '../../utils/seo';

const HomePage = () => {
  // フォーム状態管理
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // フォーム送信処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);
      formBody.append('recipient', 'info@brains-g.com');

      const response = await fetch('https://readdy.ai/api/form/d4p3gm2r20r5k3vfkf30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 入力変更処理
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // SEO設定
  useSEO({
    title: '佐世保市の就労継続支援B型事業所おにぎり｜利用者募集中｜グループホームここな',
    description: '佐世保市・佐々町の就労継続支援B型事業所おにぎりは利用者募集中。弁当製造で社会復帰を支援。グループホームここなは完全個室で24時間生活支援。',
    keywords: '佐世保市,就労継続支援B型,B型事業所,利用者募集,おにぎり,弁当製造,障害者就労支援,グループホーム,ここな,佐々町,長崎県,社会復帰支援',
    canonical: `${getBaseUrl()}/`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "name": "就労継続支援B型事業所おにぎり",
          "image": "https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/6dabeec2e9be537c19e7241578d8cc6c.jpeg",
          "description": "佐世保市・佐々町の就労継続支援B型事業所おにぎり。弁当製造による職業訓練で社会復帰を支援。料理経験不問。利用者募集中。",
          "@id": `${getBaseUrl()}/#onigiri`,
          "url": `${getBaseUrl()}/services#onigiri`,
          "telephone": "+81-956-59-4059",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "本田原免226-9",
            "addressLocality": "北松浦郡佐々町",
            "addressRegion": "長崎県",
            "postalCode": "857-0311",
            "addressCountry": "JP"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "佐世保市"
            },
            {
              "@type": "City",
              "name": "佐々町"
            }
          ],
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "16:30"
          }
        },
        {
          "@type": "LocalBusiness",
          "name": "グループホームここな",
          "image": "https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/2b522ec42038847049e65f1796c3713e.png",
          "description": "佐世保市大潟町のグループホームここな。約10畳の完全個室とユニットバス完備で、プライバシー重視の生活環境を提供。24時間体制の生活支援。",
          "@id": `${getBaseUrl()}/`,
          "url": `${getBaseUrl()}/`,
          "telephone": "+81-956-59-4059",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "大潟町725-87",
            "addressLocality": "佐世保市",
            "addressRegion": "長崎県",
            "postalCode": "858-0926",
            "addressCountry": "JP"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 33.1803,
            "longitude": 129.7156
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday"
            ],
            "opens": "00:00",
            "closes": "23:59"
          },
          "areaServed": {
            "@type": "City",
            "name": "佐世保市"
          }
        },
        {
          "@type": "Organization",
          "name": "株式会社ブレインズ",
          "url": `${getBaseUrl()}/`,
          "logo": "https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/2b522ec42038847049e65f1796c3713e.png",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+81-956-59-4059",
            "contactType": "customer service",
            "areaServed": "JP",
            "availableLanguage": "Japanese"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "ホーム",
              "item": `${getBaseUrl()}/`
            }
          ]
        },
        {
          "@type": "WebPage",
          "name": "佐世保市の就労継続支援B型事業所おにぎり｜利用者募集中｜グループホームここな",
          "description": "佐世保市・佐々町の就労継続支援B型事業所おにぎりは利用者募集中。弁当製造で社会復帰を支援。グループホームここなは完全個室で24時間生活支援。",
          "url": `${getBaseUrl()}/`
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold text-teal-600 cursor-pointer">
                株式会社ブレインズ
              </a>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                ホーム
              </a>
              <a href="#kokona" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                ここなについて
              </a>
              <a href="#onigiri" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                おにぎりについて
              </a>
              <a href="#services" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                サービス
              </a>
              <a href="/services" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                事業所案内
              </a>
            </nav>
            {/* デスクトップ: 電話番号表示のみ */}
            <div className="hidden md:flex items-center bg-teal-600 text-white px-6 py-2 rounded-full">
              <i className="ri-phone-line mr-2"></i>
              <span className="whitespace-nowrap">0956-59-4059</span>
            </div>
            {/* モバイル: 電話リンク */}
            <a
              href="tel:0956-59-4059"
              className="md:hidden bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
            >
              <i className="ri-phone-line mr-2"></i>
              お問い合わせ
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section - 2事業並列表示 */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              <strong>佐世保市・佐々町の就労継続支援B型事業所</strong>とグループホーム
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-4">
              <strong>就労継続支援B型事業所おにぎり</strong>（利用者募集中）＆ グループホームここな
            </p>
            <p className="text-lg text-gray-600">
              <strong>佐世保市</strong>で弁当製造による職業訓練と完全個室の生活支援
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* グループホームここな */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="relative h-64">
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/2b522ec42038847049e65f1796c3713e.png"
                  alt="グループホームここな"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 bg-teal-600 text-white px-4 py-2 rounded-full font-semibold">
                  グループホーム
                </div>
              </div>
              <div className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">グループホームここな</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  佐世保市大潟町にある完全個室のグループホーム。約10畳の個室とユニットバス完備で、プライバシーを重視した生活環境を提供します。
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-3"></i>
                    <span>完全個室（約10畳）</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-3"></i>
                    <span>ユニットバス完備</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-3"></i>
                    <span>24時間体制の生活支援</span>
                  </li>
                </ul>
                <a
                  href="#kokona"
                  className="inline-block w-full bg-teal-600 text-white px-6 py-3 rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-center font-semibold"
                >
                  詳しく見る
                </a>
              </div>
            </div>

            {/* 就労継続支援B型事業所おにぎり */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow border-4 border-green-500">
              <div className="relative h-64">
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/6dabeec2e9be537c19e7241578d8cc6c.jpeg"
                  alt="就労継続支援B型事業所おにぎり"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-lg animate-pulse">
                  <i className="ri-megaphone-line mr-2"></i>
                  利用者募集中！
                </div>
                <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-full font-semibold">
                  就労継続支援B型
                </div>
              </div>
              <div className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">就労継続支援B型事業所おにぎり</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  佐世保市・佐々町で弁当製造による職業訓練を実施。料理経験がなくても、個人のペースで始められます。<strong className="text-green-600">現在、利用者を募集中です！</strong>
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-green-600 mr-3"></i>
                    <span>弁当製造による職業訓練</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-green-600 mr-3"></i>
                    <span>未経験でも安心スタート</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-green-600 mr-3"></i>
                    <span>社会復帰に向けた支援</span>
                  </li>
                </ul>
                <a
                  href="#onigiri"
                  className="inline-block w-full bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-center font-semibold"
                >
                  詳しく見る
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* おにぎり専用セクション - 利用者募集 */}
      <section id="onigiri" className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-red-600 text-white px-6 py-3 rounded-full font-bold text-lg mb-6 animate-pulse">
              <i className="ri-megaphone-line mr-2"></i>
              利用者募集中！
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <strong>佐世保市・佐々町の就労継続支援B型事業所おにぎり</strong>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>佐世保市</strong>で弁当製造を通じて、社会復帰に向けたスキルを身につけませんか？<br />
              料理経験がなくても大丈夫。あなたのペースで始められる<strong>就労継続支援B型</strong>です。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-restaurant-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center"><strong>佐世保市</strong>で弁当製造の職業訓練</h3>
              <p className="text-gray-700 text-center leading-relaxed">
                自家栽培野菜を使った健康的な弁当づくり。盛り付けなど簡単な作業から始められる<strong>就労継続支援B型</strong>です。
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hand-heart-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">個人のペースで安心</h3>
              <p className="text-gray-700 text-center leading-relaxed">
                料理経験がなくても大丈夫。一人ひとりのできる範囲に応じてサポートする<strong>B型事業所</strong>です。
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-community-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center"><strong>佐世保市</strong>で地域貢献活動</h3>
              <p className="text-gray-700 text-center leading-relaxed">
                地域イベントへの弁当配達など、<strong>佐世保市</strong>の社会との繋がりを大切にしています。
              </p>
            </div>
          </div>

          {/* 利用対象者・利用の流れ */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-user-heart-line text-green-600 mr-3"></i>
                <strong>佐世保市</strong>の<strong>就労継続支援B型</strong>利用対象者
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl mt-1 mr-3"></i>
                  <span className="text-gray-700">障害者手帳をお持ちの方</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>佐世保市・佐々町</strong>周辺にお住まいの方</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl mt-1 mr-3"></i>
                  <span className="text-gray-700"><strong>就労継続支援B型</strong>を通じて社会復帰を目指す方</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl mt-1 mr-3"></i>
                  <span className="text-gray-700">料理経験は不問です</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-roadster-line text-green-600 mr-3"></i>
                <strong>佐世保市B型事業所</strong>の利用の流れ
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">お問い合わせ</h4>
                    <p className="text-sm text-gray-700">お電話またはフォームからお気軽にご連絡ください</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">見学</h4>
                    <p className="text-sm text-gray-700"><strong>佐世保市</strong>の事業所の雰囲気や作業内容をご覧いただけます</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">体験利用</h4>
                    <p className="text-sm text-gray-700">実際に<strong>B型事業所</strong>の作業を体験していただけます</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">利用開始</h4>
                    <p className="text-sm text-gray-700">あなたのペースで無理なく始められます</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* おにぎりCTA */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-8 rounded-2xl shadow-xl text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              <strong>佐世保市の就労継続支援B型事業所</strong>まずはお気軽に見学にお越しください
            </h3>
            <p className="text-xl text-green-100 mb-6">
              <strong>B型事業所おにぎり</strong>の雰囲気や作業内容を実際にご覧いただけます
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="hidden md:inline-flex bg-white text-green-600 px-8 py-4 rounded-full text-lg font-bold items-center">
                <i className="ri-phone-line mr-2 text-2xl"></i>
                0956-59-4059
              </div>
              <a
                href="tel:0956-59-4059"
                className="md:hidden bg-white text-green-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
              >
                <i className="ri-phone-line mr-2 text-2xl"></i>
                0956-59-4059
              </a>
              <a
                href="#contact"
                className="bg-white text-green-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
              >
                <i className="ri-mail-line mr-2"></i>
                お問い合わせフォーム
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - グループホームここな */}
      <section id="kokona" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">佐世保市のグループホームここな｜プライバシー重視の自立支援施設</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              長崎県佐世保市にあるアパート型の障害者グループホームです。一般のアパートを活用した新しい形のグループホームで、<strong>プライバシーを重視</strong>した居住環境を提供し、<strong>自立した生活</strong>をサポートします。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/2b522ec42038847049e65f1796c3713e.png"
                alt="長崎県佐世保市大潟町のグループホームここな外観 - アパート型障害者支援施設、完全個室とユニットバス完備でプライバシーを重視した自立支援環境を提供"
                className="rounded-lg shadow-lg w-full h-80 object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">プライバシーを重視した自立支援を実現</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                グループホームここなは、一般的なアパートを改装した温かみのある施設です。一人ひとりに約10畳の完全個室を提供し、一般の方と同じような居住環境で<strong>プライバシーを重視</strong>した生活を送っていただけます。
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                各居室にはユニットバス（風呂・トイレ）、冷蔵庫、エアコン、テレビ、テーブル・椅子を完備しています。
              </p>
              <div className="bg-teal-50 p-6 rounded-lg">
                <h4 className="font-bold text-teal-800 mb-4">お問い合わせ・アクセス</h4>
                <p className="text-teal-700 mb-2">〒858-0926 長崎県佐世保市大潟町725-87</p>
                <p className="text-teal-700 mb-4">TEL: 0956-59-4059</p>

                {/* 交通アクセス */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-teal-800 mb-3">交通アクセス</h4>
                  <div className="space-y-2 text-sm text-teal-700">
                    <div className="flex items-center">
                      <i className="ri-bus-line text-teal-600 mr-2"></i>
                      <span>志田バス停から徒歩12分</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-bus-line text-teal-600 mr-2"></i>
                      <span>朝日ヶ丘バス停から徒歩17分</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-train-line text-teal-600 mr-2"></i>
                      <span>松浦鉄道「大学駅」からタクシーで8分</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 食事について */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">佐世保市グループホームここなの食事サポート</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  食事は、アパートの一室を改造した専用の食堂で行います。朝食は7:30から、昼食は11:30から、夕食は16:30からとなっており、食事が終わりましたら各自お部屋にお戻りいただきます。
                </p>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/31465ca2ff517027f64c469f20e2cb83.jpeg"
                  alt="佐世保市グループホームここな専用食堂 - アパート一室を改造した温かみのある食事スペース、利用者が朝昼夕の食事を楽しむ共有空間"
                  className="rounded-lg shadow-lg w-full h-64 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">佐世保市グループホームここなの特徴｜プライバシー重視の完全個室</h2>
            <p className="text-xl text-gray-600">
              アパートを活用した独自のグループホームで、一般の方と同じような居住環境を提供
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-home-4-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">完全個室</h3>
              <p className="text-gray-700 leading-relaxed">
                約10畳の広々とした完全個室で、一般の方と同じようなプライベート空間を確保しています。
              </p>
            </div>

            <div className="text-center bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-drop-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">ユニットバス完備</h3>
              <p className="text-gray-700 leading-relaxed">
                各部屋にユニットバス（風呂・トイレ）を完備し、<strong>プライバシー</strong>を保ちながらご利用いただけます。
              </p>
            </div>

            <div className="text-center bg-gray-50 p-8 rounded-lg">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">プライバシー重視</h3>
              <p className="text-gray-700 leading-relaxed">
                一般のアパート暮らしと同じような環境で<strong>プライバシー</strong>を守ります。
              </p>
            </div>
          </div>

          {/* 居室設備詳細 */}
          <div className="bg-teal-50 p-8 rounded-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">佐世保市で充実した居室設備｜プライバシー重視の自立支援</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <i className="ri-fridge-line text-teal-600 mr-3"></i>
                    <span className="text-gray-700">冷蔵庫</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-temp-cold-line text-teal-600 mr-3"></i>
                    <span className="text-gray-700">エアコン</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-tv-line text-teal-600 mr-3"></i>
                    <span className="text-gray-700">テレビ</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-table-line text-teal-600 mr-3"></i>
                    <span className="text-gray-700">テーブル・椅子</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-drop-line text-teal-600 mr-3"></i>
                    <span className="text-gray-700">ユニットバス（風呂・トイレ）</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    <i className="ri-washing-machine-line text-teal-600 mr-2"></i>
                    洗濯機：アパート廊下に設置（共用）
                  </p>
                </div>
              </div>

              <div>
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/da2b2796355b8f01207454746e64b1c8.jpeg"
                  alt="佐世保市グループホームここな居室内部 - 約10畳の広々とした完全個室、ユニットバス・冷蔵庫・エアコン・テレビ完備でプライバシー重視の快適な生活空間"
                  className="rounded-lg shadow-lg w-full h-64 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">佐世保市・佐々町の自立支援サービス｜プライバシー重視の総合ケア</h2>
            <p className="text-xl text-gray-600">
              <strong>プライバシーを重視</strong>した生活支援から社会復帰支援まで、総合的なケアを提供します
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* グループホーム */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-home-heart-line text-xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">グループホームここな</h3>
              </div>
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/da2b2796355b8f01207454746e64b1c8.jpeg"
                alt="佐世保市グループホームここな居室 - 24時間体制の生活支援と個別ケアを提供、約10畳完全個室でプライバシー重視の障害者自立支援施設"
                className="w-full h-48 object-cover object-top rounded-lg mb-6"
              />
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span>24時間体制での生活支援</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span>個別支援計画に基づいたケア</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span><strong>プライバシーを重視</strong>した居住環境</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mt-1 mr-3"></i>
                  <span>一般のアパート暮らしと同様の環境</span>
                </li>
              </ul>
            </div>

            {/* 就労継続支援 B型事業所おにぎり */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-restaurant-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">就労継続支援B型事業所おにぎり</h3>
              </div>
              <div className="flex flex-col gap-2 mb-6">
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/6dabeec2e9be537c19e7241578d8cc6c.jpeg"
                  alt="佐々町就労継続支援B型事業所おにぎり弁当製造作業 - 利用者が盛り付けや調理補助を行う様子、自家栽培野菜を使用した健康的な弁当づくりで社会復帰を支援"
                  className="w-full h-48 object-cover object-top rounded-lg"
                />
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/aae74b4aa2e439a4d4eed37aa15bff79.jpeg"
                  alt="就労継続支援B型事業所おにぎり弁当製造 - 料理経験がない方でも個人のペースで始められる盛り付け作業、職業訓練を通じた社会復帰支援"
                  className="w-full h-48 object-cover object-top rounded-lg"
                />
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/f484fe15dff9222b53b8ab000f1293c8.jpeg"
                  alt="就労継続支援B型事業所おにぎり完成弁当 - 自家栽培野菜を使用した健康的な弁当、地域配達サービスで地域社会に貢献"
                  className="w-full h-48 object-cover object-top rounded-lg"
                />
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">所在地：長崎県北松浦郡佐々町本田原免226-9</p>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span>弁当製造業務による職業訓練</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span>自家栽培野菜を使用した健康的な弁当</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span>弁当の注文受付と地域配達サービス</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span>社会復帰に向けたスキル習得</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800 font-semibold mb-2">
                  <i className="ri-heart-line mr-2"></i>地域貢献活動
                </p>
                <p className="text-sm text-green-700">
                  地域の運動会などのイベントに弁当配達を行い、地域社会との繋がりを大切にした社会貢献活動を実施しています。
                </p>
              </div>
              <div className="mt-6">
                <a
                  href="/services#onigiri"
                  className="inline-block bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  就労継続支援B型事業所おにぎりの詳細
                </a>
              </div>
            </div>

            {/* 就労継続支援 野菜栽培B型支援 */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-plant-line text-xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">就労継続支援 野菜栽培B型支援</h3>
              </div>
              <div className="flex flex-col gap-2 mb-6">
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/119871acdb33bd361665bfec879be398.jpeg"
                  alt="就労継続支援野菜栽培B型支援と高校生の協働作業 - 地方高校生と利用者が一緒に野菜栽培を行う世代間交流、農業を通じた社会復帰支援と教育の両面で地域貢献"
                  className="w-full h-48 object-cover object-top rounded-lg"
                />
                <img
                  src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/285c3b9c3d4458edbedab27553624d94.jpeg"
                  alt="就労継続支援野菜栽培B型支援作業風景 - 農家借用畑での野菜栽培作業、農作業を通じた体力向上と職業訓練で社会復帰を支援、無人販売所で地域に新鮮野菜を提供"
                  className="w-full h-48 object-cover object-top rounded-lg"
                />
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">農家から借用した畑での野菜栽培</p>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-3"></i>
                  <span>農業を通じた職業訓練と社会復帰支援</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-3"></i>
                  <span>無人販売所での地域への野菜提供</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-orange-600 mr-3"></i>
                  <span>弁当製造への食材供給で循環型事業</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800 font-semibold mb-2">
                  <i className="ri-heart-line mr-2"></i>社会貢献活動
                </p>
                <p className="text-sm text-orange-700">
                  農業を通じた職業訓練により、利用者の皆様の社会復帰を支援し、<strong>地域社会に貢献</strong>しています。
                </p>
              </div>
              <div className="mt-6">
                <a
                  href="/services#vegetable"
                  className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  野菜栽培B型支援の取り組み
                </a>
              </div>
            </div>
          </div>

          {/* Daily Life Section */}
          <section className="py-20 bg-white mt-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">佐世保市グループホームここなでの生活｜プライバシー重視の1日</h2>
                <p className="text-xl text-gray-600">
                  プライベートを大切にしながら、必要な時にサポートを受けられる安心の生活
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-sun-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">朝（7:30-9:00）</h4>
                  <p className="text-sm text-gray-700">就労継続支援 野菜栽培B型支援</p>
                </div>

                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-briefcase-line text-2xl text-green-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">日中（9:00-16:30）</h4>
                  <p className="text-sm text-gray-700">B型事業所での作業活動、11:30から昼食</p>
                </div>

                <div className="text-center p-6 bg-orange-50 rounded-lg">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-restaurant-2-line text-2xl text-orange-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">夕方（16:30-18:00）</h4>
                  <p className="text-sm text-gray-700">帰宅・食堂での夕食後は自室へ</p>
                </div>

                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-moon-line text-2xl text-purple-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">夜（18:00-22:00）</h4>
                  <p className="text-sm text-gray-700">個室でのプライベート時間・就寝準備</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form Section */}
          <section className="py-20 bg-white mt-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせ</h2>
                <p className="text-xl text-gray-600">
                  ご質問やご相談がございましたら、お気軽にお問い合わせください
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg">
                <form 
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <p className="flex items-center">
                        <i className="ri-check-line mr-2"></i>
                        お問い合わせを受け付けました。ご連絡ありがとうございます。
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <p className="flex items-center">
                        <i className="ri-error-warning-line mr-2"></i>
                        送信に失敗しました。もう一度お試しください。
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </form>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-teal-600 mt-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-white mb-6">佐世保市グループホームここな｜見学・お問い合わせはお気軽に</h2>
              <p className="text-xl text-teal-100 mb-8 leading-relaxed">
                グループホームここなでは、いつでも見学を受け付けております。<br />
                アパート型グループホームの特徴をぜひ実際にご覧ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {/* デスクトップ: 電話番号表示のみ */}
                <div className="hidden md:inline-flex bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center">
                  <i className="ri-phone-line mr-2"></i>
                  0956-59-4059
                </div>
                {/* モバイル: 電話リンク */}
                <a
                  href="tel:0956-59-4059"
                  className="md:hidden bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  0956-59-4059
                </a>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-gray-900 text-white py-12 mt-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-bold mb-4">グループホームここな</h4>
                  <div className="text-gray-300">
                    <p>〒858-0926</p>
                    <p>長崎県佐世保市大潟町725-87</p>
                    <p>TEL: 0956-59-4059</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold mb-4">関連事業所</h4>
                  <div className="text-gray-300">
                    <a
                      href="/services#onigiri"
                      className="block font-semibold mb-2 hover:text-white transition-colors cursor-pointer"
                    >
                      就労継続支援B型事業所おにぎり
                    </a>
                    <p>〒857-0311</p>
                    <p>長崎県北松浦郡佐々町本田原免226-9</p>
                    <a
                      href="/services#vegetable"
                      className="block font-semibold mt-4 hover:text-white transition-colors cursor-pointer"
                    >
                      就労継続支援 野菜栽培B型支援
                    </a>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold mb-4">運営会社</h4>
                  <div className="text-gray-300">
                    <p className="font-semibold">株式会社ブレインズ</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
                <p>&copy; 2024 グループホームここな. All rights reserved.</p>
                <a
                  href="https://readdy.ai/?origin=logo"
                  className="text-gray-400 hover:text-white transition-colors"
                  rel="nofollow"
                >
                  Website Builder
                </a>
              </div>
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
